/**
 * Department & Co-Agent Type Registry
 *
 * This file is the **single source of truth** for department types, co-agent types,
 * and the team hierarchy used across the frontend.
 *
 * ## Naming Convention
 *
 * Each department and co-agent has a **slug** (URL-safe identifier) and a **name** (display label).
 *
 * - **slug** is used in URLs, API calls, and database records.
 *   Changing a slug requires updating: backend models, core service, migrations, and frontend.
 *
 * - **name** is the human-readable label shown in the UI.
 *   Names can be changed freely in this file — no backend/core changes needed.
 *
 * ## Department Types (slug → name)
 *
 * | Slug         | Name            |
 * |--------------|-----------------|
 * | marketing    | Marketing       |
 * | creative     | Creative Assets |
 * | businessops  | Business Ops    |
 * | strategy     | Strategy        |
 *
 * ## Co-Agent Types (slug → name)
 *
 * | Slug      | Name      | Department  |
 * |-----------|-----------|-------------|
 * | geo       | GEO       | marketing   |
 * | ads       | Ads       | marketing   |
 * | content   | Content   | marketing   |
 * | forge     | Forge     | businessops |
 * | products  | Products  | creative    |
 * | brand-dna | Brand DNA | creative    |
 *
 * Synced with: Python `Department.Type`, `CoAgentType` enums in `backend/apps/skillflows/models.py`
 *              Core `CoAgentPipelineType` in `core/src/config/coAgentSteps.ts`
 */

import type { WorkflowType } from "@/features/department";
import type { DepartmentSlug, TeamSlug } from "@/features/department/lib/departmentRoutes";
import { cdnUrl } from "@/lib/utils";

// ── Department Types ────────────────────────────────────────────────────────

/**
 * Department type slugs — used in URLs and backend.
 * Changing these requires backend migration + core update.
 */
export const DepartmentType = {
  MARKETING: "marketing",
  CREATIVE: "creative",
  BUSINESSOPS: "businessops",
  STRATEGY: "strategy",
} as const;

export type DepartmentType = (typeof DepartmentType)[keyof typeof DepartmentType];

/**
 * Department display names — safe to change without backend/core updates.
 */
export const DEPARTMENT_NAMES: Record<DepartmentType, string> = {
  marketing: "Marketing",
  creative: "Creative Assets",
  businessops: "Business Ops",
  strategy: "Strategy",
};

/** @deprecated Use DEPARTMENT_NAMES instead */
export const DEPARTMENT_LABELS = DEPARTMENT_NAMES;

// ── Co-Agent Types ──────────────────────────────────────────────────────────

/**
 * Co-agent type slugs — used in URLs, API calls, and database.
 * Each slug matches its team slug in the department hierarchy.
 * Changing these requires backend migration + core update.
 */
export const CoAgentType = {
  GEO: "geo",
  ADS: "ads",
  CONTENT: "content",
  FORGE: "forge",
  PRODUCTS: "products",
  BRAND_DNA: "brand-dna",
  // Phase 15-12 — Saga lives under marketing/social. The slug
  // `'social'` is the team slug in the URL hierarchy + the
  // ``Unit.name`` on the Django side (Phase 15-1 migration 0035).
  SOCIAL: "social",
} as const;

export type CoAgentType = (typeof CoAgentType)[keyof typeof CoAgentType];

/**
 * Co-agent display names — safe to change without backend/core updates.
 */
export const COAGENT_NAMES: Record<CoAgentType, string> = {
  geo: "GEO",
  ads: "Ads",
  content: "Content",
  forge: "Forge",
  products: "Products",
  "brand-dna": "Brand DNA",
  social: "Social",
};

/** Maps co-agent type slug → department type slug. */
export const COAGENT_TO_DEPARTMENT: Record<CoAgentType, DepartmentType> = {
  geo: DepartmentType.MARKETING,
  ads: DepartmentType.MARKETING,
  content: DepartmentType.MARKETING,
  forge: DepartmentType.BUSINESSOPS,
  products: DepartmentType.CREATIVE,
  "brand-dna": DepartmentType.CREATIVE,
  social: DepartmentType.MARKETING,
};

// ── Department / Team hierarchy (frontend routing) ──────────────────────────

/** Which workspace component renders this team. */
export type TeamComponent =
  | "department" // Legacy department route target, now redirected before render
  | "products" // ProductsPage (product catalog & store management)
  | "branddna" // Legacy Brand DNA route target, now redirected before render
  | "ads" // AdsWorkspace
  | "content-studio" // ContentStudioWorkspace
  | "analytics" // AnalyticsDashboardPage
  | "forge" // Legacy Forge route target, now redirected before render
  | "social-studio" // SocialStudioWorkspace
  | "placeholder";

export type TeamConfig = {
  slug: TeamSlug;
  label: string;
  desc: string;
  coAgentType?: WorkflowType;
  component: TeamComponent;
  imageUrl?: string;
  locked?: boolean;
};

export type DepartmentGroupConfig = {
  slug: DepartmentSlug;
  label: string;
  desc: string;
  icon: string; // lucide icon name
  teams: TeamConfig[];
};

export const DEPARTMENT_GROUPS: DepartmentGroupConfig[] = [
  {
    slug: "marketing",
    label: DEPARTMENT_NAMES.marketing,
    desc: "Customer acquisition, SEO, ads, and content automation",
    icon: "Megaphone",
    teams: [
      {
        slug: "ads",
        label: COAGENT_NAMES.ads,
        desc: "Create converting ads for Meta, TikTok and Google",
        coAgentType: "ads",
        component: "ads",
        imageUrl: cdnUrl("/card-marketing-ads.jpg"),
      },
      {
        slug: "content",
        label: COAGENT_NAMES.content,
        desc: "Automated content publishing",
        coAgentType: "content",
        component: "content-studio",
        imageUrl: cdnUrl("/card-marketing-content.jpg"),
      },
      {
        slug: "geo",
        label: COAGENT_NAMES.geo,
        desc: "Generative Engine Optimization",
        coAgentType: "geo",
        component: "department",
        imageUrl: cdnUrl("/card-marketing-geo.jpg"),
        locked: true,
      },
      // Phase 15-12 — Saga's Social Studio surface. Wired to the
      // unified-agent-runtime via the Phase 15 migration: the team
      // page mounts <SocialStudioWorkspace /> which connects the
      // SagaCompanionPanel to a `social-studio-copilot` session.
      // Saga (DB-authored, runtime_target='unified') dispatches to
      // 7 sub-agents (researcher / character / brief / storyboard /
      // video / calendar / publisher).
      {
        slug: "social",
        label: COAGENT_NAMES.social,
        desc: "Social media strategy and growth — research, character, briefs, video, publishing",
        coAgentType: "social",
        component: "social-studio",
        imageUrl: cdnUrl("/card-marketing-social.jpg"),
      },
    ],
  },
  {
    slug: "businessops",
    label: DEPARTMENT_NAMES.businessops,
    desc: "Operations, analytics, finance, and support",
    icon: "Building2",
    teams: [
      {
        slug: "forge",
        label: COAGENT_NAMES.forge,
        desc: "Pitch deck co-pilot",
        coAgentType: "forge",
        component: "forge",
        imageUrl: cdnUrl("/card-businessops-forge.jpg"),
      },
      {
        slug: "analytics",
        label: "Analytics",
        desc: "Business intelligence and dashboards",
        component: "analytics",
        imageUrl: cdnUrl("/card-businessops-analytics.jpg"),
        locked: true,
      },
      {
        slug: "support",
        label: "Customer Success",
        desc: "Customer success automation",
        component: "placeholder",
        imageUrl: cdnUrl("/card-businessops-support.jpg"),
        locked: true,
      },
      {
        slug: "supply",
        label: "Supply & Inventory",
        desc: "Inventory management and supply chain",
        component: "placeholder",
        imageUrl: cdnUrl("/card-businessops-supply.jpg"),
      },
      {
        slug: "finance",
        label: "Finance",
        desc: "Invoicing, expenses, and revenue tracking",
        component: "placeholder",
        imageUrl: cdnUrl("/card-businessops-finance.jpg"),
        locked: true,
      },
    ],
  },
  {
    slug: "creative",
    label: DEPARTMENT_NAMES.creative,
    desc: "Products, brand identity, and multimedia assets",
    icon: "Palette",
    teams: [
      {
        slug: "products",
        label: COAGENT_NAMES.products,
        desc: "Product catalog and store management",
        coAgentType: "products",
        component: "products",
        imageUrl: cdnUrl("/card-creative-products.jpg"),
      },
      {
        slug: "brand-dna",
        label: COAGENT_NAMES["brand-dna"],
        desc: "Brand identity, logos, colors, and strategy",
        coAgentType: "brand-dna",
        component: "branddna",
        imageUrl: cdnUrl("/card-creative-brand-dna.jpg"),
      },
      {
        slug: "voice",
        label: "Voice",
        desc: "Text-to-speech and voice cloning",
        component: "placeholder",
        imageUrl: cdnUrl("/card-creative-voice.jpg"),
      },
      {
        slug: "images",
        label: "Images",
        desc: "Marketing images and brand visuals",
        component: "placeholder",
        imageUrl: cdnUrl("/card-creative-images.jpg"),
      },
    ],
  },
  {
    slug: "strategy",
    label: DEPARTMENT_NAMES.strategy,
    desc: "Fundraising, pitch decks, and investor strategy",
    icon: "Rocket",
    teams: [
      {
        slug: "forge",
        label: COAGENT_NAMES.forge,
        desc: "Pitch deck co-pilot",
        coAgentType: "forge",
        component: "forge",
      },
    ],
  },
];

/** Lookup a department group by slug. */
export function getDepartmentGroup(slug: string): DepartmentGroupConfig | undefined {
  return DEPARTMENT_GROUPS.find((d) => d.slug === slug);
}

/** Lookup a team within a department by slug. */
export function getTeamConfig(deptSlug: string, teamSlug: string): TeamConfig | undefined {
  return getDepartmentGroup(deptSlug)?.teams.find((t) => t.slug === teamSlug);
}
